// ==================== GURUKUL — ENROLL A LEARNER (wizard) ====================
// Step 1 (about you + how many learners), then the shared consent flow (one step per policy, acknowledgments,
// optional consents + signature, review). On submit the server creates the learners AND saves the signed consent in
// ONE step; if the consent cannot be saved, no learner is created. Learners get an anonymous ID only.
import { showToast } from '../components/toasts.js';
import { runConsentFlow } from './gurukul-consent-flow.js';
import { state } from '../state.js';
import { navigateOk } from '../utils/okAccess.js';

const esc = t => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export function getGurukulEnrollPage() {
  return `
    <div class="card" id="gkEnrollWizard"><p class="empty-state">Loading…</p></div>`;
}

export async function initGurukulEnrollPage() {
  const box = document.getElementById('gkEnrollWizard');
  await runConsentFlow(box, {
    heading: 'Enroll a learner',
    submitLabel: 'Sign and enroll',
    firstStep: {
      label: 'About you',
      render: w => `
        <p class="page-intro">You are enrolling as <strong>${esc(state.user?.name || '')}</strong> (${esc(state.user?.email || '')}). Learners are identified by an anonymous ID only, such as L100001 — no child names or other details are collected. You become the learner's primary guardian. You read and agree to each policy <strong>once</strong>, however many learners you enroll now.</p>
        <div class="form-grid-row">
          <div class="form-group"><label for="gkWzCount">How many learners are you enrolling?</label><input type="number" id="gkWzCount" min="1" max="10" value="${w.count || 1}" style="max-width:120px;"></div>
          <div class="form-group"><label for="gkWzRel">Your relationship to them</label><input type="text" id="gkWzRel" maxlength="50" value="${esc(w.relationship || 'parent')}" placeholder="mother, father, guardian…" style="max-width:260px;width:100%;"></div>
        </div>`,
      bind: w => {
        w.count = w.count || 1; w.relationship = w.relationship || 'parent';
        document.getElementById('gkWzCount').addEventListener('input', e => { w.count = parseInt(e.target.value, 10) || 1; });
        document.getElementById('gkWzRel').addEventListener('input', e => { w.relationship = e.target.value; });
      },
      validate: w => (w.count < 1 || w.count > 10) ? 'Choose between 1 and 10 learners' : (!String(w.relationship || '').trim() ? 'Tell us your relationship to the learner' : null),
    },
    reviewRows: w => `<tr><th style="width:34%;text-align:left;">Enrolling</th><td>${w.count} learner(s), as ${esc(w.relationship)}</td></tr>`,
    confirmText: w => `Sign and enroll ${w.count} learner(s)? Your signature is recorded permanently.`,
    submit: ({ acceptances, signature, w }) => api('/api/gurukul/enroll-learners', { method: 'POST', body: JSON.stringify({ count: w.count, relationship_label: w.relationship.trim(), signature_name: signature, acceptances }) }),
    onSuccess: (r, el) => {
      el.innerHTML = `
        <h2>Enrolled</h2>
        <p class="page-intro">Your learner ID${r.learnerIds.length > 1 ? 's are' : ' is'}:</p>
        <p style="font-size:1.3rem;font-weight:700;">${r.learnerIds.map(esc).join(', ')}</p>
        <p class="page-intro">Your signed consent is recorded as <strong>${esc(r.consent_no)}</strong>. Your team's coordinator has been told. The learner's LMS account is set up in a later step by the Gurukul team.</p>
        <div class="btn-group" style="display:flex;gap:8px;"><button type="button" id="gkWzHome">Go to my learners</button><button type="button" class="secondary" id="gkWzAnother">Enroll another</button></div>`;
      document.getElementById('gkWzHome').addEventListener('click', () => { try { sessionStorage.removeItem('gk_learner'); } catch (e) { /* optional */ } navigateOk('/gurukul/learner-home'); });
      document.getElementById('gkWzAnother').addEventListener('click', () => initGurukulEnrollPage());
    },
  });
  showToast; // (kept for parity with other pages)
}
